import { useEffect, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import PdfPanel from '../../Snapshot/ComparePdfsModal/PdfPanel';
import styles from '../styles';

const modalStyles = styles.compareModal;
const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize='small' />;

/* eslint-disable no-unused-vars */
type SnapshotComparisonLetterModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (sections: string[]) => void;
  submission: string | null;
  letterIndex: number;
  letterCount: number;
  pdfUrl: string;
  loading: boolean;
  error: string;
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  sections: string[];
  sectionOptions: string[];
};
/* eslint-enable no-unused-vars */

const SnapshotComparisonLetterModal = ({
  open,
  onClose,
  onConfirm,
  submission,
  letterIndex,
  letterCount,
  pdfUrl,
  loading,
  error,
  canGoPrevious,
  canGoNext,
  onPrevious,
  onNext,
  sections,
  sectionOptions
}: SnapshotComparisonLetterModalProps) => {
  const [draftSections, setDraftSections] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setDraftSections(sections.filter(Boolean));
    }
  }, [open, sections]);

  const options = Array.from(new Set([...sectionOptions, ...draftSections]));

  const handleConfirm = () => {
    onConfirm(draftSections.filter(Boolean));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalStyles.letterDialogPaper }}>
      <Box sx={modalStyles.root}>
        <Box sx={modalStyles.content}>
          <Box sx={modalStyles.letterBody}>
            <Box sx={modalStyles.letterPdfPane}>
              <PdfPanel
                title={submission ? `Letter — ${submission}` : 'Letter document'}
                pdfUrl={pdfUrl}
                isLoading={loading}
                iframeTitle='Letter document'
                error={error}
                emptyMessage='No letter loaded'
                hideHeader
              />
            </Box>

            <Box sx={modalStyles.letterFormPane}>
              <Typography variant='subtitle1' gutterBottom>
                Sections Changed
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Select the label sections that changed. Click OK to apply to this row.
              </Typography>
              <Autocomplete
                multiple
                size='small'
                options={options}
                value={draftSections}
                onChange={(_, value) => setDraftSections(value)}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <li {...props}>
                    <Checkbox
                      icon={checkboxIcon}
                      checkedIcon={checkboxCheckedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </li>
                )}
                renderInput={params => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    label='Sections'
                    placeholder={draftSections.length ? '' : 'Select sections'}
                  />
                )}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={[modalStyles.footer, ...(letterCount > 1 ? [modalStyles.footerWithNav] : [])]}>
          {letterCount > 1 && (
            <Box sx={modalStyles.letterNav}>
              <IconButton
                size='small'
                onClick={onPrevious}
                disabled={!canGoPrevious || loading}
                aria-label='Previous letter'>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant='body2' color='text.secondary'>
                {letterIndex + 1} / {letterCount}
              </Typography>
              <IconButton
                size='small'
                onClick={onNext}
                disabled={!canGoNext || loading}
                aria-label='Next letter'>
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}
          <Box sx={modalStyles.letterActions}>
            <Button onClick={onClose} variant='outlined' size='small'>
              Cancel
            </Button>
            <Button onClick={handleConfirm} variant='contained' size='small'>
              OK
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SnapshotComparisonLetterModal;
